(ns app.domain.util
  "Small helper functions for domain entities"
  (:require #?(:clj [datomic.api :as d]
               :cljs [app.front.entities :as entities])
            [core.db.entity.util :as eu]
            [potpuri.core :as p]))

(defn entity-id-set
  "Extract the ids of entities as a set"
  [entities]
  (->> entities
       (keep :db/id)
       set))

(defn in-state? [state entity]
  (= state (:domain/state entity)))

(defn in-state-fn
  "Make a predicate function that returns true if the state of
  an entity is one of the given arguments"
  [& states]
  (let [states-set (set states)]
    (fn [e] (-> e :domain/state states-set boolean))))

(defn in-process-state-fn
  "Make a predicate function that returns true if the state of
  an entity is one of the given arguments"
  [& states]
  (let [states-set (set states)]
    (fn [e] (-> e :process/state states-set boolean))))

(defmulti is-active?
  "check if an entity is active

  Semantically, becoming inactive means being effectively removed from the system
  except for special cases. In other words, almost like being removed."
  #_{:clj-kondo/ignore [:redundant-fn-wrapper]}
  (fn [entity] (:entity/type entity)))

(defmethod is-active? :default [_] true)

(defn compare-nil-last
  "Like `clojure.core/compare` except nils are sorted last"
  [a b]
  (cond
    (and (nil? a) (nil? b)) 0
    (nil? a)                1
    (nil? b)                -1
    :else                   (compare a b)))


(defn- find-descendant-naive [pred parent]
  (let [children (:entity/_parent parent)]
    (or (-> children (p/find-first pred))
        (some (partial find-descendant-naive pred) children))))


(defn entity-for-db-of [parent id]
  #?(:clj (d/entity (d/entity-db parent) id)
     :cljs (entities/find-entity (.-db parent) id)))

(defn is-descendant? [{expected-ancestor-id :db/id
                       :as                  expected-ancestor}
                      child]
  (let [{parent-id :db/id :as parent} (if (eu/is-type? :entity.type/attachment-version child)
                                        (:attachment/_versions child)
                                        (:entity/parent child))]
    (or (some-> expected-ancestor-id (= parent-id))
        (and parent (recur expected-ancestor parent)))))


(defn- id-pred? [pred]
  (and (map? pred)
       (-> pred keys (= [:db/id]))))


(defn find-descendant [pred ancestor]
  (if (id-pred? pred)
    (let [descendant (entity-for-db-of ancestor (:db/id pred))]
      (when (is-descendant? ancestor descendant)
        descendant))
    (find-descendant-naive pred ancestor)))

(defn entities->entity-db
  "Put entities in a map keyed by id"
  [entities]
  (p/index-by :db/id entities))


(defn flatten-state-order-list
  "Given a state order vector for `state-order-fn`, produce
  a flat vector suitable for e.g. schema enum definitions."
  [states]
  (mapcat (fn [state-or-states]
            (if (keyword? state-or-states)
              [state-or-states]
              state-or-states))
          states))


(defn state-order-fn
  "Make a function that returns an order number based on the
  state of an entity, in the order specified in `states`.

  `states` should be a sequence of keywords and maps of keywords"
  [states]
  (fn [{state :domain/state}]
    (->> states
         (map-indexed vector)
         (some (fn [[ix v]]
                 (when (or (state v)
                           (= v state))
                   ix))))))

(def latest-entity
  "Takes a sequence of entities and returns an entity with newest :entity/created timestamp."
  (comp last (partial sort-by :entity/created)))

(defn at-least-two?
  "Returns true if the sequence has at least two elements. Avoiding (count ...) for lazy sequences."
  [s]
  (boolean (and (seq s) (seq (rest s)))))
